import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { countDecimalPlaces } from '@flipster/utils/num';

import { adjustLeverageModalBunja } from '../state';

const Placeholder = () => (
  <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>-</span>
);

export const LeverageStats = () => {
  const {
    wsTargetPositionAtom,
    maxPositionAtLeverageAtom,
    liqPriceAtLeverageAtom,
    quoteCurrencyAtom,
    pricePrecisionAtom,
    positionBracketAtom,
  } = useBunja(adjustLeverageModalBunja);

  useAtomValue(positionBracketAtom);

  const wsTargetPosition = useAtomValue(wsTargetPositionAtom);
  const maxPosition = useAtomValue(maxPositionAtLeverageAtom);
  const liqPrice = useAtomValue(liqPriceAtLeverageAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);

  const isModifying = wsTargetPosition != null;
  const isCross = isModifying && wsTargetPosition.marginType === 'Cross';

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      })}
    >
      <div className={flex({ justify: 'space-between', align: 'center' })}>
        <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <T id='Position_leverage.r260609.Max_position_this_leverage' />
        </span>
        <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
          {maxPosition != null ? (
            <>
              <Formatted.Number value={maxPosition} toFixed precision={countDecimalPlaces(maxPosition)} />{' '}
              {quoteCurrency}
            </>
          ) : (
            <Placeholder />
          )}
        </span>
      </div>

      {isModifying && (
        <div className={flex({ justify: 'space-between', align: 'center' })}>
          {isCross ? (
            <Tooltip.Root placement='top-start'>
              <Tooltip.Trigger asChild>
                <span
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-3',
                    display: 'inline-block',
                    borderBottomWidth: 1,
                    borderBottomStyle: 'dashed',
                    borderBottomColor: 'border.default.level-3',
                    cursor: 'help',
                  })}
                >
                  <T id='Position.r250401.Est_liquidation_price' />
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}>
                <T id='Trade.r260506.Estimated_liquidation_price_desc' />
              </Tooltip.Content>
            </Tooltip.Root>
          ) : (
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <T id='Assets.r250401.Liquidation_price' />
            </span>
          )}
          <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
            {liqPrice != null ? (
              <>
                <Formatted.Number value={liqPrice} toFixed precision={pricePrecision} /> {quoteCurrency}
              </>
            ) : (
              <Placeholder />
            )}
          </span>
        </div>
      )}
    </div>
  );
};
